import React, { useState } from 'react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Hero from '../components/Hero';
import ContentSection from '../components/ContentSection';
import FourOptions from '../components/FourOptions';
import Mantra from '../components/Mantra';
import Footer from '../components/Footer';
import '../styles/App.css';
import '../styles/Periodization.css';
import HeroImage from '../assets/images/app/periodization_hero.png';
import MantraImage from '../assets/images/Mantra_1.png';
import SamImage from '../assets/images/drops/Sam_Begin_1.jpg';

// Interactive Three Levels Visualization Component
function ThreeLevelsVisualization() {
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);

  const mesocycles = [
    { phase: 1, name: 'Build the Base', weeks: '1-4', focus: 'Foundation' },
    { phase: 2, name: 'Add Volume', weeks: '5-8', focus: 'More reps' },
    { phase: 3, name: 'Get Stronger', weeks: '9-12', focus: 'Heavy loads' }
  ];

  const weekSchedule = {
    Mon: { 
      type: 'strength', 
      title: 'Full-Body A – Push + Squat',
      muscles: ['Chest', 'Shoulders', 'Quads', 'Glutes']
    },
    Tue: { 
      type: 'endurance', 
      title: 'Aerobic Base',
      muscles: ['Run', 'Swim', 'or Bike']
    },
    Wed: { 
      type: 'strength', 
      title: 'Full-Body B – Pull + Hinge',
      muscles: ['Back', 'Biceps', 'Hamstrings', 'Core']
    },
    Thu: { 
      type: 'recovery', 
      title: 'Active Recovery',
      muscles: ['Mobility', 'Stretching']
    },
    Fri: { 
      type: 'strength', 
      title: 'Full-Body C – Total Body Circuit',
      muscles: ['Full Body', 'Conditioning']
    },
    Sat: { 
      type: 'endurance', 
      title: 'Long Aerobic Session',
      muscles: ['Run', 'Swim', 'or Bike']
    },
    Sun: { 
      type: 'recovery', 
      title: 'Rest Day',
      muscles: ['Recovery', 'Sleep']
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'strength': return '#FF6B6B';
      case 'endurance': return '#00B8FF';
      case 'recovery': return 'rgba(255,255,255,0.4)';
      default: return '#FFF';
    }
  };

  return (
    <div className="content-container faq-sub-section">
      <h2 className="content-header">
        The Three <span style={{ color: 'red' }}>Levels</span>
      </h2>
      <p className="content-description">
        Your training is organized into three layers—like nesting boxes. Tap to explore.
      </p>
      
      <div className="three-levels-wrapper">
        {/* Macrocycle - Outermost */}
        <div className="macro-box">
          <div className="macro-header">
            <div className="macro-header-left">
              <div className="level-badge macro">The Big Picture</div>
              <div className="level-title">Your Season or Year</div>
            </div>
            <div className="level-duration">3-12 months</div>
          </div>
          <div className="level-description">
            The overall goal: build a foundation, get stronger, peak for competition, or maintain fitness.
          </div>

          {/* Mesocycle Row */}
          <div className="meso-box">
            <div className="macro-header">
              <div className="macro-header-left">
                <div className="level-badge meso">Training Blocks</div>
                <div className="level-title">Focused Phases</div>
              </div>
              <div className="level-duration">4-6 weeks each</div>
            </div>
            
            {/* Mesocycle blocks - hoverable */}
            <div className="meso-blocks">
              {mesocycles.map((meso) => (
                <div 
                  key={meso.phase} 
                  className={`meso-block ${hoveredBlock === meso.phase ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredBlock(meso.phase)}
                  onMouseLeave={() => setHoveredBlock(null)}
                  onClick={() => setHoveredBlock(hoveredBlock === meso.phase ? null : meso.phase)}
                >
                  <div className="meso-block-weeks">Weeks {meso.weeks}</div>
                  <div className="meso-block-name">{meso.name}</div>
                  {hoveredBlock === meso.phase && (
                    <div className="meso-block-focus">{meso.focus}</div>
                  )}
                </div>
              ))}
            </div>

            <div className="meso-note">
              Each block has a focus. You'll gradually do more or go harder, then take an easier week to recover.
            </div>

            {/* Microcycle Row */}
            <div className="micro-box">
              <div className="macro-header">
                <div className="macro-header-left">
                  <div className="level-badge micro">Your Week</div>
                  <div className="level-title">Daily Workouts</div>
                </div>
                <div className="level-duration">7 days</div>
              </div>

              {/* Week blocks - hoverable */}
              <div className="day-blocks">
                {Object.entries(weekSchedule).map(([day, info]) => (
                  <div 
                    key={day} 
                    className={`day-block ${hoveredDay === day ? 'active' : ''}`}
                    onMouseEnter={() => setHoveredDay(day)}
                    onMouseLeave={() => setHoveredDay(null)}
                    onClick={() => setHoveredDay(hoveredDay === day ? null : day)}
                  >
                    <div className="day-block-name">{day}</div>
                    <div className={`day-block-type ${info.type}`}>{info.type}</div>
                  </div>
                ))}
              </div>

              {/* Workout details on hover */}
              {hoveredDay && (
                <div className="workout-details">
                  <div className="workout-header" style={{ color: getTypeColor(weekSchedule[hoveredDay].type) }}>
                    {hoveredDay} • {weekSchedule[hoveredDay].type}
                  </div>
                  
                  {/* Workout structure: Warmup | Main | Core */}
                  <div className="workout-structure">
                    {/* Warmup */}
                    <div className="workout-section warmup">
                      <div className="workout-section-label">Warmup</div>
                      <div className="workout-section-content">Dynamic Prep</div>
                    </div>
                    
                    {/* Main Workout */}
                    <div className="workout-section main" style={{ borderLeft: `3px solid ${getTypeColor(weekSchedule[hoveredDay].type)}` }}>
                      <div className="workout-section-label">Main Workout</div>
                      <div className="workout-section-content">{weekSchedule[hoveredDay].title}</div>
                      <div className="workout-tags">
                        {weekSchedule[hoveredDay].muscles.map((muscle, idx) => (
                          <span key={idx} className="workout-tag">{muscle}</span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Core Finisher */}
                    <div className="workout-section core">
                      <div className="workout-section-label">Core Finisher</div>
                      <div className="workout-section-content">Abs & Stability</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Combined explanation section - full width */}
        <div className="content-card how-it-works-card">
          <h3 className="content-title">How It All Works</h3>
          <div className="content-body">
            <p>
              <strong>Year → Blocks → Weeks → Workouts.</strong> Each layer has a purpose, and together they guide you forward.
            </p>
            
            <h4>Always Progressing</h4>
            <p>
              Each phase builds on the last. Hard weeks are followed by easier weeks—your body adapts, recovers, and comes back stronger.
            </p>
            
            <h4>Works for Everyone</h4>
            <p style={{ marginBottom: '8px' }}>The structure stays the same, but adapts to you:</p>
            <div className="works-for-tags">
              <span className="works-for-tag">Beginners → gradual build-up</span>
              <span className="works-for-tag">Athletes → peak on time</span>
              <span className="works-for-tag">Rehab → safe progression</span>
              <span className="works-for-tag">Longevity → sustainable patterns</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Periodization() {
  // Introduction section
  const introductionContent = {
    sectionTitle: (
      <>
        What is <span style={{ color: 'red' }}>Periodization?</span>
      </>
    ),
    sectionDescription:
      'A fancy word for a simple idea: plan your training in phases so you keep getting stronger without burning out.',
    items: [
      {
        title: 'Training with a Plan',
        imageSrc: SamImage,
        imagePosition: 'right',
        body1: (
          <>
            <p>
              Instead of doing random workouts and hoping for results, periodization gives you a roadmap. You'll know what to focus on this week, this month, and beyond.
            </p>
            <p>
              Think of it like building a house: first the foundation, then the walls, then the roof. Each phase builds on the last.
            </p>
            <p style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <strong style={{ color: '#FF0000' }}>About the Founder</strong><br />
              Sam DuRegger built Begin Strength on the principles he learned as an Exercise Science major at the University of Tulsa, a varsity football letterwinner, and a certified coach through both USA Track & Field and USA Triathlon. This isn't theory—it's how athletes actually train.
            </p>
          </>
        ),
      },
    ],
  };

  // Benefits section - 4 box grid
  const benefitsContent = {
    sectionTitle: (
      <>
        Why It <span style={{ color: 'red' }}>Works</span>
      </>
    ),
    sectionDescription: null,
    options: [
      {
        title: 'No More Plateaus',
        body: (
          <p>
            Changing your focus every few weeks keeps your body guessing and adapting. You won't get stuck doing the same thing forever.
          </p>
        ),
      },
      {
        title: 'Fewer Injuries',
        body: (
          <p>
            Built-in recovery weeks let your body catch up. Push hard, then rest. That's how you stay healthy long-term.
          </p>
        ),
      },
      {
        title: 'Peak When It Matters',
        body: (
          <p>
            Got a race, competition, or goal date? Your training builds toward it so you're at your best when it counts.
          </p>
        ),
      },
      {
        title: 'No More Guessing',
        body: (
          <p>
            Every day has a purpose. You'll always know what to do and why you're doing it.
          </p>
        ),
      },
    ],
  };

  return (
    <div className="home">
      <SEO
        title="Understanding Periodization"
        description="The simple system that turns random workouts into real progress. Plan your training in phases so you keep getting stronger without burning out."
        image="/og-images/periodization.png"
        path="/periodization"
      />
      <Header />
      <main>
        <Hero
          backgroundImage={HeroImage}
          headerText={
            <>
              <span className="periodization-hero-title">Understanding<br /><span style={{ color: 'red' }}>Periodization</span></span>
            </>
          }
          paragraphText={
            <>
              <p>
                The simple system that turns random workouts into real progress.
              </p>
            </>
          }
          primaryButtonText="View Programs"
          primaryButtonLink="/programs"
          secondaryButtonText="Download App"
          secondaryButtonLink="/app"
        />

        <ContentSection
          sectionTitle={introductionContent.sectionTitle}
          sectionDescription={introductionContent.sectionDescription}
          items={introductionContent.items}
          sectionClass="faq-sub-section"
          showNumbers={false}
        />

        <ThreeLevelsVisualization />

        <FourOptions
          sectionTitle={benefitsContent.sectionTitle}
          sectionDescription={benefitsContent.sectionDescription}
          options={benefitsContent.options}
        />

        <Mantra
          backgroundImage={MantraImage}
          mantraText="Train Smarter, Not Harder"
        />

        <Footer />
      </main>
    </div>
  );
}

export default Periodization;
